import { createCheckoutContext, getContextInvalidationReason, isPickupShipping } from '../context.js'
import { acceptPickupPointMessage, buildCheckoutMapUrl, closeMapSession, createMapSession } from '../map.js'
import { parsePickupPointFieldValue, requiresGermanPostnummer, serializePickupPointFieldValue } from '../pickupPoint.js'
import { createRwdView } from '../rwdView.js'
import { createCheckoutSessionStore } from '../sessionStore.js'
import { PICKUP_VALIDATION_CODES, validatePickupPoint } from '../validation.js'
import { createRwdSelectors, getCustomRwdSelectorKeys } from './rwdSelectors.js'
import { createRwdStateReader, readRwdDomState } from './rwdState.js'

const validationMessages = {
  [PICKUP_VALIDATION_CODES.CONFIG_MISSING_FIELD]: 'Błąd konfiguracji sklepu: brak pola wyboru punktu odbioru.',
  [PICKUP_VALIDATION_CODES.STATE_PENDING]: 'Nie udało się potwierdzić aktualnej metody dostawy. Spróbuj ponownie.',
  [PICKUP_VALIDATION_CODES.POINT_REQUIRED]: 'Wybierz punkt odbioru DHL.',
  [PICKUP_VALIDATION_CODES.POINT_TYPE_REQUIRED]: 'Nie udało się potwierdzić typu niemieckiego punktu DHL. Wybierz punkt ponownie.',
  [PICKUP_VALIDATION_CODES.POSTNUMMER_REQUIRED]: 'Dla dostawy do DHL Packstation w Niemczech podaj Postnummer.',
  [PICKUP_VALIDATION_CODES.PERSISTENCE_UNCONFIRMED]: 'Nie udało się potwierdzić zapisu punktu. Wróć do danych dostawy i spróbuj ponownie.',
}

function persistedFieldValue(basket, fieldId) {
  const fields = basket?.additional_fields
  return fields && typeof fields === 'object' ? fields[String(fieldId)] : null
}

function parsePersistedSnapshot(value) {
  const snapshot = parsePickupPointFieldValue(value)
  if (!snapshot || typeof value !== 'string') return snapshot
  try {
    const raw = JSON.parse(value)
    return { ...snapshot, country: String(raw?.country ?? '').trim().toUpperCase() }
  } catch {
    return snapshot
  }
}

function matchesSnapshotCountry(snapshot, context) {
  const country = String(snapshot?.country ?? '').trim().toUpperCase()
  return country === '' || country === context.countryIso
}

function snapshotsMatch(left, right, countryIso) {
  if (!left || !right) return false
  return serializePickupPointFieldValue(left, countryIso) === serializePickupPointFieldValue(right, countryIso)
}

function sameForwardDomState(left, right) {
  if (!left || !right) return false
  return [
    'stage',
    'shippingId',
    'paymentId',
    'countryId',
    'shippingControlPresent',
    'paymentControlPresent',
    'countryControlPresent',
    'differentAddress',
    'isCod',
  ].every(key => left[key] === right[key])
}

export async function initializeRwdCheckout({
  bootstrap,
  configLoader,
  root = document,
  window: windowObject = window,
  logger = console,
  timeoutMs = 8000,
} = {}) {
  const settings = await configLoader?.load?.()
  if (!settings || typeof settings !== 'object') {
    logger?.warn?.('[DHL checkout] Brak konfiguracji checkoutu RWD.')
    return null
  }

  const selectorOverrides = windowObject.octolizeDhl?.rwd ?? {}
  let selectors
  try {
    selectors = createRwdSelectors(selectorOverrides, root)
  } catch (error) {
    logger?.error?.('[DHL checkout] Nieprawidłowa konfiguracja selektorów RWD.', error)
    return null
  }

  const customSelectorKeys = getCustomRwdSelectorKeys(selectorOverrides)
  const scopeShippingToList = customSelectorKeys.has('shippingList')
  const view = createRwdView({ root, selectors, customSelectorKeys })
  const reader = createRwdStateReader({
    root,
    selectors,
    frontApi: () => windowObject.frontAPI,
    timeoutMs,
    scopeShippingToList,
    prepareAddress: ({ stage, shippingId }) => view.setPickupAddressMode(
      stage === 'address' && isPickupShipping(settings, shippingId),
    ),
  })
  let sessionStorage = null
  try {
    sessionStorage = windowObject.sessionStorage
  } catch (error) {
    logger?.warn?.('[DHL checkout] Pamięć sesji jest niedostępna.', error)
  }
  const store = createCheckoutSessionStore({ storage: sessionStorage, shopId: bootstrap?.shopId, logger })
  let context = null
  let snapshot = null
  let persistence = 'cache'
  let mapSession = null
  let destroyed = false
  const syncTimers = new Set()
  let pending = true
  let requiresFreshState = false
  let syncedDomState = null
  let configurationError = null
  let lastReportedConfigurationError = null

  const reportConfigurationError = (message) => {
    configurationError = message
    if (lastReportedConfigurationError !== message) {
      logger?.error?.('[DHL checkout] Nieobsługiwana konfiguracja motywu RWD.', { message })
      lastReportedConfigurationError = message
    }
    view.showError(message)
  }

  const clearScheduledSyncs = () => {
    syncTimers.forEach(timer => windowObject.clearTimeout(timer))
    syncTimers.clear()
  }

  const writeAddressField = () => {
    if (context?.stage !== 'address') return true
    const value = snapshot ? serializePickupPointFieldValue(snapshot, context.countryIso) : ''
    return view.setPickupField(settings.pickupPointFieldId, value)
  }

  const openMap = () => {
    if (context?.stage !== 'address') return
    if (pending && requiresFreshState) {
      view.showError(validationMessages[PICKUP_VALIDATION_CODES.STATE_PENDING])
      return
    }
    const url = buildCheckoutMapUrl(settings, context)
    if (!url) {
      view.showError('Nie udało się przygotować mapy punktów DHL. Spróbuj ponownie.')
      return
    }
    const iframe = view.openMap(url)
    if (!iframe) return
    view.clearError()
    mapSession = createMapSession({ iframe, origin: url.origin, context, pointType: url.searchParams.get('ptype') })
  }

  const updatePostnummer = (event) => {
    if (!snapshot || context?.stage !== 'address' || !requiresGermanPostnummer(snapshot, context.countryIso)) return
    snapshot = { ...snapshot, postnummer: String(event?.target?.value ?? '').trim() }
    store.saveSelection(context, snapshot, { pendingPersistence: true })
    writeAddressField()
    view.clearError()
  }

  const render = () => {
    if (!context || !isPickupShipping(settings, context.shippingId)) {
      configurationError = null
      lastReportedConfigurationError = null
      view.removeWidgets()
      view.clearError()
      return
    }
    const result = view.render({
      shippingId: context.shippingId,
      stage: context.stage,
      pickupPoint: snapshot,
      requirePostnummer: requiresGermanPostnummer(snapshot, context.countryIso),
      onOpen: openMap,
      onPostnummer: updatePostnummer,
    })
    configurationError = result.configurationError
    if (configurationError) {
      reportConfigurationError(configurationError)
    } else {
      lastReportedConfigurationError = null
    }
  }

  const sync = async () => {
    if (destroyed) return
    pending = true
    try {
      const state = await reader.read()
      if (!state || destroyed) return
      if (state.empty || state.stage === 'complete') {
        snapshot = null
        context = null
        store.clear()
        view.removeWidgets()
        view.closeMap()
        pending = false
        requiresFreshState = false
        clearScheduledSyncs()
        return
      }

      const nextContext = createCheckoutContext(state)
      const invalidation = context ? getContextInvalidationReason(context, nextContext) : null
      if (invalidation) {
        snapshot = null
        persistence = 'cache'
        store.markCleared(nextContext)
        closeMapSession(mapSession)
        mapSession = null
        view.closeMap()
      }
      context = nextContext
      syncedDomState = state.domState

      const cached = store.loadForContext(context)
      let cacheStatus = cached.status
      if (cached.status === 'stale') {
        snapshot = null
        store.markCleared(context)
        cacheStatus = 'cleared'
      }
      const persisted = parsePersistedSnapshot(persistedFieldValue(state.basket, settings.pickupPointFieldId))
      const usableCachedSelection = cacheStatus === 'selection' && cached.verifiedCart
      const pendingCachedSelection = usableCachedSelection
        && cached.pendingPersistence
        && !snapshotsMatch(cached.snapshot, persisted, context.countryIso)
      if (cacheStatus !== 'cleared' && persisted && matchesSnapshotCountry(persisted, context) && !pendingCachedSelection) {
        snapshot = { ...persisted, country: context.countryIso }
        persistence = 'persisted'
        store.saveSelection(context, snapshot)
      } else if ((!snapshot || pendingCachedSelection) && usableCachedSelection) {
        snapshot = cached.snapshot
        persistence = 'cache'
      } else if (!persisted && context.stage === 'summary') {
        persistence = 'missing'
      }

      if (!isPickupShipping(settings, context.shippingId)) {
        snapshot = null
      }
      if (snapshot && !requiresGermanPostnummer(snapshot, context.countryIso)) {
        snapshot = { ...snapshot, postnummer: '' }
      }
      writeAddressField()
      render()
      pending = false
      requiresFreshState = false
      clearScheduledSyncs()
    } catch (error) {
      const domState = readRwdDomState(root, selectors, { scopeShippingToList })
      if (!['address', 'summary'].includes(domState.stage)) {
        pending = false
        requiresFreshState = false
        configurationError = null
        view.removeWidgets()
        view.clearError()
        return
      }
      logger?.warn?.('[DHL checkout] Nie udało się zsynchronizować stanu RWD.', error)
      view.showError(error?.message || validationMessages[PICKUP_VALIDATION_CODES.STATE_PENDING])
    }
  }

  const scheduleSync = (delays = [0]) => {
    clearScheduledSyncs()
    delays.forEach((delay) => {
      const delayTimer = windowObject.setTimeout(() => {
        syncTimers.delete(delayTimer)
        if (destroyed) return
        const syncTimer = windowObject.setTimeout(() => {
          syncTimers.delete(syncTimer)
          sync()
        }, 25)
        syncTimers.add(syncTimer)
      }, delay)
      syncTimers.add(delayTimer)
    })
  }

  const validateForward = () => {
    const currentDomState = readRwdDomState(root, selectors, { scopeShippingToList })
    if (['basket', 'purchase-choice'].includes(currentDomState.stage)) {
      view.clearError()
      return true
    }
    if (configurationError) {
      view.showError(configurationError)
      return false
    }
    if (!context || context.stage !== currentDomState.stage || (pending && (requiresFreshState || !sameForwardDomState(syncedDomState, currentDomState)))) {
      view.showError(validationMessages[PICKUP_VALIDATION_CODES.STATE_PENDING])
      return false
    }
    writeAddressField()
    const result = validatePickupPoint({
      settings,
      context,
      snapshot,
      persistence,
      fieldAvailable: context.stage !== 'address' || Boolean(view.getPickupField(settings.pickupPointFieldId)),
    })
    if (!result.valid) {
      view.showError(validationMessages[result.code] ?? validationMessages[PICKUP_VALIDATION_CODES.STATE_PENDING])
      scheduleSync()
      return false
    }
    view.clearError()
    return true
  }

  const onSubmit = (event) => {
    const form = event.target
    if (!form?.matches?.(`${selectors.basketForm},${selectors.addressForm},${selectors.summaryForm}`)) return
    if (event.submitter?.matches?.(selectors.backButton)) return
    if (customSelectorKeys.has('forwardButton') && event.submitter && !event.submitter.matches(selectors.forwardButton)) return
    if (!validateForward()) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }
  const onChange = (event) => {
    const target = event?.target
    const relevantSelector = [
      selectors.shippingControl,
      selectors.paymentControl,
      selectors.basketCountry,
      selectors.addressCountry,
      selectors.deliveryCountry,
      selectors.differentAddress,
      selectors.codInput,
    ].join(',')
    if (!target?.matches?.(relevantSelector)) return
    pending = true
    requiresFreshState = true
    closeMapSession(mapSession)
    mapSession = null
    view.closeMap()
    reader.invalidate()
    scheduleSync([0, 150, 500, 1000])
  }
  const onInput = (event) => {
    if (event.target?.matches?.('.octolize_dhl_rwd_postnummer_input')) updatePostnummer(event)
  }
  const onClick = (event) => {
    if (view.isCloseTarget(event.target)) {
      closeMapSession(mapSession)
      mapSession = null
      view.closeMap()
    }
  }
  const onMessage = (event) => {
    if (context?.stage !== 'address') return
    const point = acceptPickupPointMessage(event, mapSession, context)
    if (!point) return
    const postnummer = requiresGermanPostnummer(point, context.countryIso)
      ? String(snapshot?.postnummer ?? '').trim() : ''
    snapshot = { ...point, postnummer }
    persistence = 'cache'
    store.saveSelection(context, snapshot, { pendingPersistence: true })
    writeAddressField()
    closeMapSession(mapSession)
    mapSession = null
    view.closeMap()
    view.clearError()
    render()
  }
  const onPageShow = () => {
    pending = true
    requiresFreshState = true
    closeMapSession(mapSession)
    mapSession = null
    view.closeMap()
    reader.invalidate()
    scheduleSync([0, 150])
  }

  root.addEventListener('submit', onSubmit, true)
  root.addEventListener('change', onChange, true)
  root.addEventListener('input', onInput, true)
  root.addEventListener('blur', onInput, true)
  root.addEventListener('click', onClick, true)
  windowObject.addEventListener('message', onMessage)
  windowObject.addEventListener('pageshow', onPageShow)

  const ownUiSelector = '#octolize-dhl-rwd-map,.octolize_dhl_rwd_widget,.octolize_dhl_rwd_error'
  const checkoutFormSelector = [selectors.basketForm, selectors.addressForm, selectors.summaryForm].join(',')
  const isElement = node => node?.nodeType === 1
  const isWithinCheckoutForm = node => isElement(node) && (
    node.matches?.(checkoutFormSelector) || node.closest?.(checkoutFormSelector)
  )
  const containsCheckoutForm = node => isElement(node) && (
    node.matches?.(checkoutFormSelector) || node.querySelector?.(checkoutFormSelector)
  )
  const isOwnMutation = ({ target, addedNodes, removedNodes }) => {
    if (target?.closest?.(ownUiSelector)) return true
    const changedNodes = [...addedNodes, ...removedNodes].filter(isElement)
    return changedNodes.length > 0 && changedNodes.every(node => node.matches?.(ownUiSelector))
  }
  const isCheckoutMutation = ({ type, target, addedNodes, removedNodes }) => {
    if (type === 'attributes') {
      return target === root.body || isWithinCheckoutForm(target)
    }
    if (isWithinCheckoutForm(target)) return true
    return [...addedNodes, ...removedNodes].some(containsCheckoutForm)
  }
  const observer = new windowObject.MutationObserver((mutations) => {
    const externalMutations = mutations.filter(mutation => !isOwnMutation(mutation))
    if (!externalMutations.some(isCheckoutMutation)) return
    pending = true
    closeMapSession(mapSession)
    mapSession = null
    view.closeMap()
    reader.invalidate()
    scheduleSync()
  })
  observer.observe(root.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked', 'value', 'disabled', 'class'],
  })
  await sync()

  return {
    sync,
    validateForward,
    destroy() {
      destroyed = true
      clearScheduledSyncs()
      observer.disconnect()
      view.setPickupAddressMode(false)
      root.removeEventListener('submit', onSubmit, true)
      root.removeEventListener('change', onChange, true)
      root.removeEventListener('input', onInput, true)
      root.removeEventListener('blur', onInput, true)
      root.removeEventListener('click', onClick, true)
      windowObject.removeEventListener('message', onMessage)
      windowObject.removeEventListener('pageshow', onPageShow)
      closeMapSession(mapSession)
      view.closeMap()
    },
  }
}
